import { createUseStyles, useDarkMode } from '@glu/theming';
import React from 'react';
import locale from '@glu/locale';
import classnames from 'classnames';
import Badge from './Badge';
import styles from './SelectString.styles';
import { DropdownActions, PanelOption } from './Dropdown';

const useStyles = createUseStyles(styles);

interface Props {
  applySelection(selection: PanelOption[]): void;
  disabled?: boolean;
  format?: (value: PanelOption[]) => string;
  onClick(event: React.MouseEvent<HTMLButtonElement>): void;
  /** Default value for select field's button content. Required to not be empty for A11y */
  placeholder?: string;
  /** All options currently selected */
  selection: PanelOption[];
  // eslint-disable-next-line react/no-unused-prop-types
  setSelection?: DropdownActions['setSelection'];
  /** UX setting to display selection as individually clearable items */
  showBadges?: boolean;
  /** Number of options to show in the select field before fallback to "N selected" message* */
  showSelected?: number;
}

const SelectString = ({
  applySelection,
  disabled,
  format,
  onClick,
  placeholder,
  selection,
  showBadges,
  showSelected,
}: Props) => {
  const dark = useDarkMode();
  const classes = useStyles();

  let selectionString = '';
  const selectionLength = selection.length;

  if (selectionLength) {
    if (selectionLength > showSelected) {
      selectionString = `${selectionLength} ${locale.get('selected')}`;
    } else if (showBadges) {
      return (
        <>
          {selection
            .sort((a, b) => (a.name < b.name ? -1 : 1))
            .map((option, index) => (
              <Badge
                key={option.id}
                applySelection={applySelection}
                disabled={disabled}
                index={index}
                onClick={onClick}
                option={option}
                selection={selection}
              />
            ))}
        </>
      );
    } else {
      selectionString = format(selection);
    }
  }

  return (
    <button
      className={classnames(classes.placeholder, {
        [classes.dark]: dark,
        [classes.disabled]: disabled,
        [classes.selectionString]: selectionString,
      })}
      data-qa="input-value"
      onClick={onClick}
      tabIndex={-1}
      type="button"
    >
      {selectionString || placeholder}
    </button>
  );
};

export default SelectString;
